// v1.0.0 - 2026-01-27 - GSE Curva (hourly data) sheet parser

import type * as XLSX from 'xlsx'
import type { ParsedMisuraOraria, ParseResult } from './gse-types'
import { GSE_COLUMN_MAPPINGS } from './gse-types'
import { sheetToJson, getRowValue, parseNumber, parseDate, formatDateISO, parseInt } from './xlsx-utils'
import { validateMisuraOraria } from './validators'

/**
 * Parse GSE Curva Oraria sheet
 *
 * Expected columns:
 * - POD: Punto di connessione
 * - Data: DD/MM/YYYY
 * - Ora: 1-24 (GSE format) or 0-23
 * - Energia: kWh value
 *
 * Note: GSE uses 1-24 hour format, we convert to 0-23
 */
export function parseCurvaSheet(
  sheet: XLSX.WorkSheet,
  tipo: 'IMMESSA' | 'PRELEVATA'
): ParseResult<ParsedMisuraOraria[]> {
  const errors: string[] = []
  const warnings: string[] = []
  const data: ParsedMisuraOraria[] = []
  let skippedRows = 0

  // Convert sheet to JSON
  const rows = sheetToJson<Record<string, unknown>>(sheet)

  if (rows.length === 0) {
    return {
      success: false,
      data: null,
      errors: ['Foglio curva vuoto o formato non riconosciuto'],
      warnings: [],
      rowCount: 0,
      skippedRows: 0,
    }
  }

  const mapping = GSE_COLUMN_MAPPINGS.CURVA

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const rowNum = i + 2 // Excel rows start at 1, plus header

    // Extract values
    const pod = getRowValue<string>(row, mapping.pod)
    const dataRaw = getRowValue<string | Date>(row, mapping.data)
    const oraRaw = getRowValue<string | number>(row, mapping.ora)
    const energiaRaw = getRowValue<string | number>(row, mapping.energia)

    // Skip empty rows
    if (!pod && !dataRaw && !oraRaw && !energiaRaw) {
      skippedRows++
      continue
    }

    // Parse date
    const parsedDate = parseDate(dataRaw)
    const dateStr = parsedDate ? formatDateISO(parsedDate) : null

    // Parse hour (GSE uses 1-24, we use 0-23)
    let hour = parseInt(oraRaw)
    if (hour !== null) {
      // If hour is 1-24, convert to 0-23
      if (hour >= 1 && hour <= 24) {
        hour = hour === 24 ? 0 : hour - 1
      }
    }

    // Parse energia
    const energia = parseNumber(energiaRaw)

    // Build record
    const record: Partial<ParsedMisuraOraria> = {
      pod: pod?.trim(),
      date: dateStr || '',
      hour: hour ?? -1,
      tipo,
      energia_kwh: energia ?? 0,
    }

    // Validate
    const validation = validateMisuraOraria(record)

    if (!validation.valid) {
      errors.push(`Riga ${rowNum}: ${validation.errors.join(', ')}`)
      skippedRows++
      continue
    }

    if (validation.warnings.length > 0) {
      warnings.push(`Riga ${rowNum}: ${validation.warnings.join(', ')}`)
    }

    data.push(record as ParsedMisuraOraria)
  }

  // Limit error messages to avoid overwhelming output
  const MAX_ERRORS = 10
  if (errors.length > MAX_ERRORS) {
    const excess = errors.length - MAX_ERRORS
    errors.splice(MAX_ERRORS, excess, `... e altri ${excess} errori`)
  }

  return {
    success: errors.length === 0 || data.length > 0,
    data: data.length > 0 ? data : null,
    errors,
    warnings,
    rowCount: rows.length,
    skippedRows,
  }
}

/**
 * Parse wide format curva (hours as columns H1-H24)
 *
 * Expected format:
 * POD | Data | H1 | H2 | ... | H24
 */
export function parseCurvaWideFormat(
  sheet: XLSX.WorkSheet,
  tipo: 'IMMESSA' | 'PRELEVATA'
): ParseResult<ParsedMisuraOraria[]> {
  const errors: string[] = []
  const warnings: string[] = []
  const data: ParsedMisuraOraria[] = []
  let skippedRows = 0

  const rows = sheetToJson<Record<string, unknown>>(sheet)

  if (rows.length === 0) {
    return {
      success: false,
      data: null,
      errors: ['Foglio curva wide vuoto o formato non riconosciuto'],
      warnings: [],
      rowCount: 0,
      skippedRows: 0,
    }
  }

  // Detect hour columns (H1-H24, or 1-24, or 0-23)
  const hourColumns: { col: string; hour: number }[] = []
  const firstRow = rows[0]
  const keys = Object.keys(firstRow)

  for (const key of keys) {
    // Match H1-H24 or just 1-24
    const match = key.match(/^H?(\d{1,2})$/i)
    if (match) {
      let hour = Number(match[1])
      // Convert 1-24 to 0-23
      if (hour >= 1 && hour <= 24) {
        hour = hour === 24 ? 0 : hour - 1
      }
      if (hour >= 0 && hour <= 23) {
        hourColumns.push({ col: key, hour })
      }
    }
  }

  if (hourColumns.length < 24) {
    warnings.push(`Trovate solo ${hourColumns.length} colonne orarie (attese 24)`)
  }

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const rowNum = i + 2

    // Extract POD and Date
    const pod = getRowValue<string>(row, ['POD', 'Pod', 'pod'])
    const dataRaw = getRowValue<string | Date>(row, ['Data', 'DATA', 'data', 'Giorno'])

    if (!pod || !dataRaw) {
      skippedRows++
      continue
    }

    const parsedDate = parseDate(dataRaw)
    const dateStr = parsedDate ? formatDateISO(parsedDate) : null

    if (!dateStr) {
      errors.push(`Riga ${rowNum}: data non valida`)
      skippedRows++
      continue
    }

    // Extract hourly values
    for (const { col, hour } of hourColumns) {
      const energiaRaw = row[col]
      const energia = parseNumber(energiaRaw)

      if (energia === null) {
        continue // Skip null values silently
      }

      data.push({
        pod: pod.trim(),
        date: dateStr,
        hour,
        tipo,
        energia_kwh: energia,
      })
    }
  }

  return {
    success: data.length > 0,
    data: data.length > 0 ? data : null,
    errors,
    warnings,
    rowCount: rows.length,
    skippedRows,
  }
}

/**
 * Auto-detect curva format and parse
 */
export function parseCurvaAuto(
  sheet: XLSX.WorkSheet,
  tipo: 'IMMESSA' | 'PRELEVATA'
): ParseResult<ParsedMisuraOraria[]> {
  const rows = sheetToJson<Record<string, unknown>>(sheet)

  if (rows.length === 0) {
    return {
      success: false,
      data: null,
      errors: ['Foglio curva vuoto'],
      warnings: [],
      rowCount: 0,
      skippedRows: 0,
    }
  }

  // Detect format by checking first row columns
  const firstRow = rows[0]
  const keys = Object.keys(firstRow)

  // Check for wide format (H1-H24 or 1-24 columns)
  const hasHourColumns = keys.some((k) => /^H?\d{1,2}$/i.test(k))

  if (hasHourColumns) {
    return parseCurvaWideFormat(sheet, tipo)
  }

  // Default to long format
  return parseCurvaSheet(sheet, tipo)
}

/**
 * Aggregate hourly data by POD and day
 */
export function aggregateCurvaByPODDay(
  data: ParsedMisuraOraria[]
): Map<string, Map<string, number[]>> {
  // Map<POD, Map<Date, hourly[24]>>
  const result = new Map<string, Map<string, number[]>>()

  for (const record of data) {
    if (!result.has(record.pod)) {
      result.set(record.pod, new Map())
    }
    const podData = result.get(record.pod)!

    if (!podData.has(record.date)) {
      podData.set(record.date, new Array(24).fill(0))
    }
    const hourly = podData.get(record.date)!
    hourly[record.hour] += record.energia_kwh
  }

  return result
}

/**
 * Get curva statistics
 */
export function getCurvaStats(data: ParsedMisuraOraria[]): {
  total_kwh: number
  days: number
  pod_count: number
  records: number
  min_date: string
  max_date: string
} {
  let total = 0
  const dates = new Set<string>()
  const pods = new Set<string>()

  for (const record of data) {
    total += record.energia_kwh
    dates.add(record.date)
    pods.add(record.pod)
  }

  const sortedDates = [...dates].sort()

  return {
    total_kwh: total,
    days: dates.size,
    pod_count: pods.size,
    records: data.length,
    min_date: sortedDates[0] || '',
    max_date: sortedDates[sortedDates.length - 1] || '',
  }
}
